
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IconX } from "@/components/ui/Icons";
import type { Modal, ModalType } from "@/types";

interface ModalContextValue {
  openModal: (type: ModalType, props?: Record<string, unknown>) => void;
  closeModal: (id?: string) => void;
  closeAll: () => void;
}

const ModalContext = createContext<ModalContextValue>({
  openModal: () => {},
  closeModal: () => {},
  closeAll: () => {},
});

export function useModal() {
  return useContext(ModalContext);
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modals, setModals] = useState<Modal[]>([]);

  const openModal = useCallback((type: ModalType, props?: Record<string, unknown>) => {
    const id = `${type}-${Date.now()}`;
    setModals((prev) => [...prev, { id, type, props }]);
  }, []);

  const closeModal = useCallback((id?: string) => {
    setModals((prev) => id ? prev.filter((m) => m.id !== id) : prev.slice(0, -1));
  }, []);

  const closeAll = useCallback(() => setModals([]), []);

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAll }}>
      {children}
      <AnimatePresence>
        {modals.map((modal) => (
          <ModalShell key={modal.id} modal={modal} onClose={() => closeModal(modal.id)} />
        ))}
      </AnimatePresence>
    </ModalContext.Provider>
  );
}

function ModalShell({ modal, onClose }: { modal: Modal; onClose: () => void }) {
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-modal-backdrop"
        onClick={onClose}
      />
      {/* Modal content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-0 flex items-center justify-center z-modal p-4 pointer-events-none"
      >
        <div className="relative bg-surface-elevated border border-border rounded-3xl shadow-2xl w-full max-w-md pointer-events-auto max-h-[90dvh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-xl text-text-subtle hover:text-foreground hover:bg-muted transition-colors z-10"
            aria-label="Close modal"
          >
            <IconX className="w-4 h-4" />
          </button>
          <ModalContent modal={modal} onClose={onClose} />
        </div>
      </motion.div>
    </>
  );
}

function ModalContent({ modal, onClose }: { modal: Modal; onClose: () => void }) {
  // Dynamic modal routing
  switch (modal.type) {
    case "auth":
      const { GuestPrompt } = require("@/components/auth/GuestPrompt");
      return <GuestPrompt action={modal.props?.action as string} onClose={onClose} />;
    default:
      return (
        <div className="p-6 text-center text-text-muted text-sm">
          Modal type &quot;{modal.type}&quot; not implemented.
        </div>
      );
  }
}
